<template>
    <HeaderContent title="Program Detail" />
    <BCard>
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Program Name</span>
                <p>{{ programItems?.name ?? "-" }}</p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">Pax</span>
                <p>{{ programItems?.pax ?? "-" }}</p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">From</span>
                <p>
                    {{
                        programItems?.from_destination
                            ? programItems?.from_destination?.name +
                              " (" +
                              programItems?.from_destination?.code +
                              ")"
                            : "-"
                    }}
                </p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">To</span>
                <p>
                    {{
                        programItems?.to_destination
                            ? programItems?.to_destination?.name +
                              " (" +
                              programItems?.to_destination?.code +
                              ")"
                            : "-"
                    }}
                </p>
            </BCol>
        </BRow>

        <!-- 1. Transportation -->
        <div class="mb-4">
            <span class="fw-bold d-block mb-2 text-start">1. Transportation</span>
            <div class="table-responsive">
                <table class="table table-sm table-bordered">
                    <thead class="table-secondary small">
                        <tr>
                            <th width="50" class="text-left">No</th>
                            <th class="text-left">Vehicle Name</th>
                            <th class="text-left">Vehicle Type</th>
                            <th class="text-center" width="80">Pax</th>
                            <th class="text-right" width="150">Price</th>
                            <th class="text-right" width="150">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody class="small">
                        <tr
                            v-for="(item, index) in programItems?.transportation"
                            :key="index"
                        >
                            <td class="text-left">{{ index + 1 }}</td>
                            <td class="text-left">{{ item.vehicle?.name }}</td>
                            <td class="text-left">{{ item.vehicle?.type }}</td>
                            <td class="text-center">{{ item.pax }}</td>
                            <td class="text-right">{{ formatRupiah(item.price) }}</td>
                            <td class="text-right">{{ formatRupiah(item.sub_total) }}</td>
                        </tr>
                        <tr v-if="!programItems?.transportation.length">
                            <td class="text-center" colspan="6">No data available</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 2. Accomodation -->
        <div class="mb-4">
            <span class="fw-bold d-block mb-2 text-start">2. Accomodation</span>
            <div class="table-responsive">
                <table class="table table-sm table-bordered">
                    <thead class="table-secondary small">
                        <tr>
                            <th width="50" class="text-left">No</th>
                            <th class="text-left">Hotel</th>
                            <th class="text-center" width="80">Pax</th>
                            <th class="text-right" width="120">Price</th>
                            <th class="text-right" width="120">Subtotal</th>
                            <th class="text-center" width="100">Extra Bed</th>
                            <th class="text-right" width="120">Extra Bed Price</th>
                            <th class="text-right" width="150">Total Price</th>
                        </tr>
                    </thead>
                    <tbody class="small">
                        <tr
                            v-for="(item, index) in programItems?.accommodation"
                            :key="index"
                        >
                            <td class="text-left">{{ index + 1 }}</td>
                            <td class="text-left">
                                <div class="fw-bold">{{ item.name }}</div>
                                <div class="text-muted small">
                                    {{ item.destination?.name }} - {{ item.room_type }}
                                </div>
                            </td>
                            <td class="text-center">{{ item.pax }}</td>
                            <td class="text-right">{{ formatRupiah(item.price) }}</td>
                            <td class="text-right">{{ formatRupiah(item.sub_total) }}</td>
                            <td class="text-center">
                                <span
                                    class="badge"
                                    :class="item.extra_bed == 1 ? 'border border-success text-success' : 'border border-danger text-danger'"
                                    style="padding: 2px 10px; border-radius: 4px;"
                                >
                                    {{ item.extra_bed == 1 ? "Yes" : "No" }}
                                </span>
                            </td>
                            <td class="text-right">{{ formatRupiah(item.extra_bed_price) }}</td>
                            <td class="text-right fw-bold">{{ formatRupiah(item.total_price) }}</td>
                        </tr>
                        <tr v-if="!programItems?.accommodation.length">
                            <td class="text-center" colspan="8">No data available</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 3. Activity -->
        <div class="mb-4">
            <span class="fw-bold d-block mb-2 text-start">3. Activity</span>
            <div class="table-responsive">
                <table class="table table-sm table-bordered">
                    <thead class="table-secondary small">
                        <tr>
                            <th width="50" class="text-left">No</th>
                            <th class="text-left">Destination</th>
                            <th class="text-left">Activity Name</th>
                            <th class="text-center" width="80">Pax</th>
                            <th class="text-right" width="150">Price</th>
                            <th class="text-right" width="150">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody class="small">
                        <tr
                            v-for="(item, index) in programItems?.activity"
                            :key="index"
                        >
                            <td class="text-left">{{ index + 1 }}</td>
                            <td class="text-left">
                                {{ item.destination?.name }} ({{ item.destination?.code }})
                            </td>
                            <td class="text-left">{{ item.name }}</td>
                            <td class="text-center">{{ item.pax }}</td>
                            <td class="text-right">{{ formatRupiah(item.price) }}</td>
                            <td class="text-right">{{ formatRupiah(item.sub_total) }}</td>
                        </tr>
                        <tr v-if="!programItems?.activity.length">
                            <td class="text-center" colspan="6">No data available</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 4. Meals -->
        <div class="mb-4">
            <span class="fw-bold d-block mb-2 text-start">4. Meals</span>
            <div class="table-responsive">
                <table class="table table-sm table-bordered">
                    <thead class="table-secondary small">
                        <tr>
                            <th width="50" class="text-left">No</th>
                            <th class="text-left">Meal Name</th>
                            <th class="text-center" width="80">Pax</th>
                            <th class="text-right" width="150">Price</th>
                            <th class="text-right" width="150">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody class="small">
                        <tr
                            v-for="(item, index) in programItems?.meals"
                            :key="index"
                        >
                            <td class="text-left">{{ index + 1 }}</td>
                            <td class="text-left">{{ item.name }}</td>
                            <td class="text-center">{{ item.pax }}</td>
                            <td class="text-right">{{ formatRupiah(item.price) }}</td>
                            <td class="text-right">{{ formatRupiah(item.sub_total) }}</td>
                        </tr>
                        <tr v-if="!programItems?.meals.length">
                            <td class="text-center" colspan="5">No data available</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Pricing Summary -->
        <BRow class="mb-4">
            <BCol>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <tbody>
                            <tr style="font-size: 15px">
                                <td class="text-start fw-bold text-muted">Price Per Pax</td>
                                <td class="text-end fw-bold text-muted col-sm-2">
                                    {{ programItems?.price ? formatRupiah(programItems.price) : "Rp 0" }}
                                </td>
                            </tr>
                            <tr style="font-size: 17px">
                                <td class="text-start fw-bold">
                                    Total Price ({{ programItems?.pax }} Pax)
                                </td>
                                <td class="text-end fw-bold bg-success text-white col-sm-2">
                                    {{
                                        programItems?.sub_total
                                            ? formatRupiah(programItems.sub_total)
                                            : "Rp 0"
                                    }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Inclusion</span>
                <ul v-if="programItems?.inclusion" class="mt-2">
                    <li
                        v-for="(item, index) in programItems?.inclusion.split('\n')"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
                <p v-else>-</p>
            </BCol>
        </BRow>
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Exclusion</span>
                <ul v-if="programItems?.exclusion" class="mt-2">
                    <li
                        v-for="(item, index) in programItems?.exclusion.split('\n')"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
                <p v-else>-</p>
            </BCol>
        </BRow>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton
                    type="button"
                    @click="() => router.push('/programs/edit/' + route.params.uuid)"
                    variant="success"
                    class="me-2"
                >
                    <i class="fas fa-edit me-1"></i> Edit
                </BButton>
                <BButton
                    type="button"
                    @click="() => router.back()"
                    variant="secondary"
                >
                    <i class="fas fa-arrow-left me-1"></i> Back
                </BButton>
            </div>
        </template>
    </BCard>
</template>

<script setup>
import axios from "axios";
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import HeaderContent from "@/components/HeaderContent.vue";
import { BCard, BRow, BCol, BButton } from "bootstrap-vue-next";
import { onMounted } from "vue";
import { formatRupiah } from "@/utils/format";
import { useRouter } from "vue-router";

const auth = useAuthStore();
const $loading = useLoading();
const route = useRoute();
const router = useRouter();

const programItems = ref(null);
const getProgram = async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/program/detail/${route.params.uuid}`,
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;

        if (res.status) {
            const data = res.data;
            programItems.value = data;
        }
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    await getProgram();
});
</script>
